import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarDaysIcon, CheckCircle2Icon, HomeIcon, MessageCircleIcon } from 'lucide-react';
import { ThankYouConfig } from '../../types';
import { Button } from '../ui/Button';
import { BrandLogo } from '../shared/BrandLogo';
import { VideoReplay } from './VideoReplay';
import { StoredSubmission, fillMessage, whatsappDigits } from '../../utils/postSubmit';
import { MetaPixelResolved, resolveMetaPixel, trackMetaWhatsapp } from '../../utils/metaPixel';
import { DEFAULT_REPLY_NOTE, DEFAULT_WHATSAPP_MESSAGE, withThankYouDefaults } from '../../data/thankYou';

/** Se reexporta para no romper los imports existentes del constructor. */
export { DEFAULT_WHATSAPP_MESSAGE };
interface ThankYouViewProps {
  config: ThankYouConfig;
  logoUrl?: string;
  primaryColor?: string;
  /** Envío que acaba de completar ESTE visitante (nunca otro lead). */
  submission?: StoredSubmission | null;
  /**
   * Teléfono de respaldo (el del WhatsApp flotante de la landing), para que el
   * paso final funcione sin tener que repetir el número en esta pestaña.
   */
  fallbackPhone?: string;
  /** En la vista previa del constructor los botones no navegan. */
  preview?: boolean;
  /** Meta Pixel ya resuelto de la landing. Sin él, no se dispara ContactWhatsApp. */
  metaPixel?: MetaPixelResolved;
}

/**
 * Página de agradecimiento. Se usa tal cual en la ruta pública /gracias/:slug
 * y también dentro de la vista previa del constructor, para que lo que se
 * edita sea exactamente lo que verá el visitante.
 *
 * Diseñada primero para móvil: título contenido, botón de WhatsApp a ancho
 * completo con 52 px de alto, burbuja del mensaje sin desbordar y respeto del
 * área segura inferior.
 */
export function ThankYouView({
  config: stored,
  logoUrl,
  primaryColor,
  submission,
  fallbackPhone,
  preview = false,
  metaPixel
}: ThankYouViewProps) {
  // Los valores ausentes se completan con el diseño por defecto de AVA.
  const config = withThankYouDefaults(stored);
  const number = whatsappDigits(config.whatsappNumber) || whatsappDigits(fallbackPhone);
  const template = config.whatsappMessage ?? DEFAULT_WHATSAPP_MESSAGE;
  /*
   * El mensaje se construye SIEMPRE con el envío de este visitante. En la vista
   * previa del constructor no hay envío real, así que se muestran valores de
   * ejemplo claramente identificables.
   */
  const source: StoredSubmission = submission ?? (preview ? {
    contact: {
      full_name: 'Carlos Martínez',
      company: 'Reformas Carlos'
    },
    answers: [{
      questionId: 'ciudad',
      label: 'Ciudad',
      value: 'Madrid'
    }, {
      questionId: 'instagram',
      label: 'Instagram',
      value: '@reformascarlos'
    }, {
      questionId: 'publicidad',
      label: 'Inversión en publicidad',
      value: '500–1.000 €'
    }, {
      questionId: 'presupuesto',
      label: 'Presupuesto disponible',
      value: '3.000 € o más'
    }, {
      questionId: 'servicio',
      label: 'Tipo de servicio',
      value: 'Gestión AVA Completa'
    }],
    at: Date.now()
  } : {
    contact: {},
    answers: [],
    at: 0
  });
  const message = fillMessage(template, source.contact, source.answers);
  const waUrl = number ? `https://wa.me/${number}${message ? `?text=${encodeURIComponent(message)}` : ''}` : null;
  const showWhatsappStep = config.showWhatsapp && Boolean(waUrl);
  const replyNote = config.replyNote ?? DEFAULT_REPLY_NOTE;
  return <main className="flex min-h-screen w-full items-start justify-center bg-surface px-4 py-10 pb-[calc(2.5rem+env(safe-area-inset-bottom))] sm:items-center sm:px-5 sm:py-16" style={primaryColor ? {
    '--primary': primaryColor
  } as React.CSSProperties : undefined} data-id="element-911" data-mp="xyizgbm5b9iy">
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-border bg-background p-5 text-center shadow-soft sm:p-12" data-id="element-912" data-mp="u022xqhvlfu1">
        <div className="mb-7 flex justify-center" data-id="element-913" data-mp="p4mpv0roznyo">
          <BrandLogo src={logoUrl} size={26} data-id="element-914" data-mp="130qiwqqtxq9k" />
        </div>
        <span className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 sm:h-14 sm:w-14" data-id="element-915" data-mp="z0pss8udbqmo">
          {showWhatsappStep ? <MessageCircleIcon className="h-6 w-6 text-primary sm:h-7 sm:w-7" aria-hidden="true" data-id="element-916" data-mp="bb3wbt5drpid" /> : <CheckCircle2Icon className="h-6 w-6 text-primary sm:h-7 sm:w-7" aria-hidden="true" data-id="element-917" data-mp="k3p8gqeiaxy8" />}
        </span>

        {showWhatsappStep ? <>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary" data-id="element-918" data-mp="3nr7ghe017fb4">
              {config.whatsappEyebrow || 'Solo falta un paso'}
            </p>
            <h1 className="text-[26px] font-bold uppercase leading-tight tracking-tight text-navy sm:text-3xl" data-id="element-919" data-mp="39m75tnpgb6oc">
              {config.whatsappStepTitle || 'Abre WhatsApp'}
            </h1>
            <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-muted-foreground sm:text-base" data-id="element-920" data-mp="2zot1unkpsacx">
              {config.whatsappStepText}
            </p>
          </> : <>
            <h1 className="text-[24px] font-bold leading-tight tracking-tight text-navy sm:text-3xl" data-id="element-921" data-mp="3scow7lmt9q6j">
              {config.title}
            </h1>
            {config.subtitle && <p className="mx-auto mt-3 max-w-md text-[15px] font-medium text-foreground sm:text-base" data-id="element-922" data-mp="3if2deo2e5eha">
                {config.subtitle}
              </p>}
            <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-muted-foreground sm:text-base" data-id="element-923" data-mp="3ncaok4p4gi91">
              {config.text}
            </p>
          </>}

        {config.showVideo && <div className="mt-8" data-id="element-924" data-mp="2bwlyh4gkn340">
            <VideoReplay url={config.videoUrl} thumbnailUrl={config.videoThumbnailUrl} maxWidthClass="max-w-xl" data-id="element-925" data-mp="igrbbv5tx2o0" />
          </div>}

        {config.imageUrl && <img src={config.imageUrl} alt="" loading="lazy" className="mx-auto mt-8 w-full rounded-xl object-cover" data-id="element-926" data-mp="8p56cgk33ztp" />}

        <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center" data-id="element-927" data-mp="3uxwlcqm5njij">
          {showWhatsappStep && <a href={preview ? undefined : waUrl as string} target="_blank" rel="noopener noreferrer" onClick={event => {
          if (preview) {
            event.preventDefault();
            return;
          }
          // D. Se dispara antes de que el navegador siga el enlace; si
          // Meta falla, WhatsApp se abre igual (nunca se bloquea ni se
          // retrasa por esto).
          trackMetaWhatsapp(metaPixel ?? resolveMetaPixel(null));
        }} className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-6 text-base font-semibold text-white transition-transform active:scale-[0.99] sm:w-auto" data-id="element-928" data-mp="3qv9ryl9u5qj8">
              <MessageCircleIcon className="h-5 w-5" aria-hidden="true" data-id="element-929" data-mp="21dmczifhrj2t" />
              {config.whatsappLabel || 'Continuar por WhatsApp'}
            </a>}
          {config.showCalendly && <Button leftIcon={<CalendarDaysIcon className="h-4 w-4" data-id="element-931" data-mp="18s37lhhkldvr" />} onClick={() => {
          if (preview || !config.calendlyUrl) return;
          window.open(config.calendlyUrl, '_blank', 'noopener');
        }} data-id="element-930" data-mp="18qs47is1e856">
              Agendar una llamada
            </Button>}
          {config.showHome && (preview ? <Button variant="ghost" leftIcon={<HomeIcon className="h-4 w-4" data-id="element-933" data-mp="2u5fnqucs7ccm" />} data-id="element-932" data-mp="2u21ovw3btl9x">
                Volver al inicio
              </Button> : <Link to="/" data-id="element-934" data-mp="3a92jotb43w9w">
                <Button variant="ghost" leftIcon={<HomeIcon className="h-4 w-4" data-id="element-936" data-mp="3a6hhg36wjebm" />} data-id="element-935" data-mp="3a9vhv5w09dgf">
                  Volver al inicio
                </Button>
              </Link>)}
        </div>

        {config.showReplyNote !== false && replyNote && <p className="mt-5 text-[13px] leading-relaxed text-muted-foreground" data-id="element-937" data-mp="1sqwsp0emq77">
            {replyNote}
          </p>}

        {/* Vista previa del mensaje, con aspecto de burbuja de WhatsApp. */}
        {showWhatsappStep && config.showWhatsappPreview !== false && message && <section className="mt-8 text-left" data-id="element-938" data-mp="bokj303xdy26">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground" data-id="element-939" data-mp="6sviquk30d4t">
              Vista previa del mensaje
            </p>
            <div className="rounded-2xl rounded-tl-md border border-border bg-surface px-4 py-3.5" data-id="element-940" data-mp="28jgndvhibz49">
              <p className="whitespace-pre-line break-words text-[13.5px] leading-relaxed text-foreground sm:text-[14px]" data-id="element-941" data-mp="o1fxbzccswch">
                {message}
              </p>
            </div>
            {preview && <p className="mt-2 text-[12px] text-muted-foreground" data-id="element-942" data-mp="ebweuusjz4i5">
                Datos de ejemplo. En la landing publicada se usan las respuestas
                reales del visitante.
              </p>}
          </section>}

        {/* Sin número configurado el paso no puede funcionar: se avisa en el
            constructor, nunca al visitante. */}
        {preview && config.showWhatsapp && !waUrl && <p className="mt-6 rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-700" data-id="element-943" data-mp="3rj6e5nl4ooit">
            Añade el número de WhatsApp para que aparezca el botón final.
          </p>}
      </div>
    </main>;
}